import { useRouter, createFileRoute } from "@tanstack/react-router";
import { css } from "../../../styled-system/css";
import { Flex, VStack, HStack } from "../../../styled-system/jsx";
import { Button, buttonRecipe } from "../../components/core/basic/Button";
import { FaCheck, FaXmark, FaSpinner } from "react-icons/fa6";
import { useKikReachLogin } from "../../modules/kikreach/useKikReachLogin";
import { useCurrentUserProfile } from "../../modules/auth/api/useCurrentUserProfile";
import { useTranslation } from "react-i18next";

/**
 * Consent page for connecting to KikReach (TikTok).
 * Shows what data will be shared before initiating SSO flow.
 */
const KikReachConnect = () => {
  const { t } = useTranslation(["kikreach"]);
  const router = useRouter();
  const { data: userProfile } = useCurrentUserProfile();
  const email = userProfile?.email;

  const loginMutation = useKikReachLogin();

  const handleConnect = () => {
    loginMutation.mutate(undefined, {
      onSuccess: (result) => {
        window.location.href = result.redirect_url;
      },
    });
  };

  const handleCancel = () => {
    router.history.back();
  };

  const errorMessage =
    loginMutation.error instanceof Error ? loginMutation.error.message
    : loginMutation.isError ? t(($) => $.kikreach["Connection failed"])
    : "";

  return (
    <Flex
      role="status"
      aria-live="polite"
      aria-atomic="true"
      gap="small"
      direction="column"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      padding="large"
    >
      {loginMutation.isIdle && (
        <VStack gap="medium" alignItems="center" maxWidth="500px">
          <div
            className={css({
              fontSize: "xxlarge",
              fontWeight: "bold",
              textAlign: "center",
            })}
          >
            {t(($) => $.kikreach["Connect to Trellis TikTok"])}
          </div>
          <div
            className={css({
              color: "gray.700",
              fontSize: "large",
              textAlign: "center",
            })}
          >
            {t(($) => $.kikreach["Trellis will share the following information with KikReach:"])}
          </div>
          <VStack
            gap="small"
            alignItems="flex-start"
            borderRadius="md"
            width="100%"
            padding="medium"
            backgroundColor="gray.100"
          >
            <div>
              <span className={css({ fontWeight: "bold" })}>{t(($) => $.kikreach["Your email:"])}</span>{" "}
              <span>{email || "—"}</span>
            </div>
            <div>
              <span className={css({ fontWeight: "bold" })}>{t(($) => $.kikreach["Your user ID"])}</span>
            </div>
          </VStack>
          <a
            href="https://gotrellis.com/privacy-policy/"
            target="_blank"
            rel="noreferrer"
            className={buttonRecipe({ variant: "link", size: "small" })}
          >
            {t(($) => $.kikreach["Privacy Policy"])}
          </a>
          <HStack gap="medium" marginTop="medium">
            <Button variant="secondary" onPress={handleCancel}>
              {t(($) => $.kikreach["Cancel"])}
            </Button>
            <Button onPress={handleConnect}>{t(($) => $.kikreach["Connect"])}</Button>
          </HStack>
        </VStack>
      )}

      {loginMutation.isPending && (
        <>
          <div
            className={css({
              color: "primary",
              fontSize: "xxlarge",
              animation: "spin 1s linear infinite",
            })}
            role="img"
            aria-label={t(($) => $.kikreach["Loading icon"])}
          >
            <FaSpinner />
          </div>
          <div className={css({ fontSize: "large", fontWeight: "bold" })}>
            {t(($) => $.kikreach["Connecting to Trellis TikTok..."])}
          </div>
          <div className={css({ color: "gray.600", textAlign: "center" })}>
            {t(($) => $.kikreach["Please wait while we authenticate you."])}
          </div>
        </>
      )}

      {loginMutation.isSuccess && (
        <>
          <div
            className={css({ color: "success", fontSize: "xxlarge" })}
            role="img"
            aria-label={t(($) => $.kikreach["Success icon"])}
          >
            <FaCheck />
          </div>
          <div className={css({ fontSize: "large", fontWeight: "bold" })}>{t(($) => $.kikreach["Connected!"])}</div>
          <div className={css({ color: "gray.600", textAlign: "center" })}>
            {t(($) => $.kikreach["Redirecting to Trellis TikTok..."])}
          </div>
        </>
      )}

      {loginMutation.isError && (
        <>
          <div
            className={css({ color: "error", fontSize: "xxlarge" })}
            role="img"
            aria-label={t(($) => $.kikreach["Error icon"])}
          >
            <FaXmark />
          </div>
          <div className={css({ fontSize: "large", fontWeight: "bold" })}>
            {t(($) => $.kikreach["Connection Failed"])}
          </div>
          <div className={css({ textAlign: "center" })} role="alert">
            {errorMessage}
          </div>
          <HStack gap="medium" marginTop="medium">
            <Button variant="secondary" onPress={handleCancel}>
              {t(($) => $.kikreach["Go Back"])}
            </Button>
            <Button onPress={handleConnect} colorScheme="fuchsia">
              {t(($) => $.kikreach["Try Again"])}
            </Button>
          </HStack>
        </>
      )}
    </Flex>
  );
};

export const Route = createFileRoute("/connect/tiktok")({
  component: KikReachConnect,
});
